import React, { useEffect, useMemo, useState } from "react";
import DynamicForm from "../../../components/DynamicForm.jsx";
import { getApp } from "firebase/app";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref as sRef } from "firebase/storage";
import { auth, db, storage } from "../../../firebase/firebase";
import {
  listenMarketplaceItemsByStatus,
  checkStorageUserWrite,
  checkStorageAdminWrite,
  approveMarketplaceItemWithRelease,
  updateMarketplaceItemStatus,
  updateMarketplaceItemFields,
  makeFormConfigFromItemForPreview,
  deleteMarketplaceItem,
} from "../../../services/data";

// helper : privilégie release.docxPath, sinon fallback templatePath
async function getDocxDownloadURL(item) {
  if (item?.release?.docxPath) {
    return await getDownloadURL(sRef(storage, item.release.docxPath));
  }
  if (item?.templatePath) {
    return await getDownloadURL(sRef(storage, item.templatePath));
  }
  throw new Error("Aucun template DOCX disponible (release/templatePath).");
}

export default function MarketplaceTab({ user }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminDebug, setAdminDebug] = useState(null);
  const [sub, setSub] = useState("pending"); // pending | published | rejected
  const [q, setQ] = useState("");
  const [pending, setPending] = useState([]);
  const [published, setPublished] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activePreviewTab, setActivePreviewTab] = useState("meta");
  const [formConfig, setFormConfig] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [thumbUrl, setThumbUrl] = useState(null);
  const [galleryUrls, setGalleryUrls] = useState([]);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const app = getApp();
        const effectiveBucket = sRef(storage, "/").bucket;
        console.log("[owner-debug]", {
          authUid: auth.currentUser?.uid || null,
          projectId: app?.options?.projectId,
          storageBucketCfg: app?.options?.storageBucket,
          envBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          effectiveBucket,
        });
      } catch (e) {
        console.warn("[owner-debug] bucket check failed:", e);
      }
    })();
  }, []);

  const previewFields = useMemo(() => {
    if (!formConfig?.fields) return [];
    return Object.values(formConfig.fields)
      .map((cfg) => ({
        key: cfg.key,
        label: cfg.label || cfg.key,
        type: cfg.type || "text",
        required: !!cfg.required,
        options: cfg.options || [],
        placeholder: cfg.placeholder || "",
        order: Number(cfg.order ?? 0),
        group_key: cfg.group_key || "",
      }))
      .sort((a, b) => a.order - b.order);
  }, [formConfig]);

  const previewSections = useMemo(
    () => (Array.isArray(formConfig?.sections) ? formConfig.sections : []),
    [formConfig]
  );

  const onTest = async (it) => {
    try {
      const url = await getDocxDownloadURL(it);
      window.open(url, "_blank");
    } catch (e) {
      alert(e.message || "Impossible d’ouvrir le DOCX");
    }
  };

  const onViewJson = async (it) => {
    try {
      if (it?.release?.configSnapshot) {
        setJsonText(JSON.stringify(it.release.configSnapshot, null, 2));
        setJsonOpen(true);
        return;
      }
      const p = it?.release?.configPath;
      if (p) {
        const url = await getDownloadURL(sRef(storage, p));
        const res = await fetch(url);
        const txt = await res.text();
        setJsonText(txt);
        setJsonOpen(true);
        return;
      }
      alert("Aucune configuration trouvée dans la release.");
    } catch (e) {
      console.error(e);
      alert("Erreur chargement JSON: " + (e?.message || e));
    }
  };

  const copyJSONToClipboard = async () => {
    try {
      if (!formConfig) return;
      await navigator.clipboard.writeText(
        JSON.stringify(formConfig, null, 2)
      );
      alert("Config copiée dans le presse-papiers.");
    } catch {
      alert("Copie impossible sur ce navigateur.");
    }
  };

  const downloadJSON = () => {
    if (!formConfig) return;
    const blob = new Blob([JSON.stringify(formConfig, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `config-${selected?.id || "modele"}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const closeJson = () => {
    setJsonOpen(false);
    setJsonText("");
  };

  // check admin
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) return;
      const OWNER_UID = import.meta.env.VITE_OWNER_UID;
      try {
        const ref = doc(db, "owner_admins", user.uid);
        const snap = await getDoc(ref);
        if (!alive) return;
        const envOwner = !!OWNER_UID && user.uid === OWNER_UID;
        setIsAdmin(snap.exists() || envOwner);
        setAdminDebug({
          uid: user.uid,
          path: ref.path,
          exists: snap.exists(),
          envOwner,
        });
      } catch (e) {
        if (!alive) return;
        setIsAdmin(false);
        setAdminDebug({
          uid: user.uid,
          path: `owner_admins/${user.uid}`,
          error: e?.message || String(e),
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!isAdmin) return;
    const uns = [];
    // Owner: voit tout le monde (pas de ownerUid)
    uns.push(
      listenMarketplaceItemsByStatus({ status: "pending_review" }, setPending)
    );
    uns.push(
      listenMarketplaceItemsByStatus({ status: "published" }, setPublished)
    );
    uns.push(
      listenMarketplaceItemsByStatus({ status: "rejected" }, setRejected)
    );
    return () => uns.forEach((u) => u && u());
  }, [isAdmin]);

  // charge la vignette pour l’item sélectionné
  useEffect(() => {
    let stop = false;
    const loadAssets = async () => {
      setThumbUrl(null);
      setGalleryUrls([]);
      if (!selected) return;

      if (selected.releaseUrls?.thumbUrl) {
        setThumbUrl(selected.releaseUrls.thumbUrl);
      } else if (selected.thumbPath) {
        try {
          const url = await getDownloadURL(sRef(storage, selected.thumbPath));
          if (!stop) setThumbUrl(url);
        } catch {
          if (!stop) setThumbUrl(null);
        }
      }

      const releaseGallery = Array.isArray(selected.releaseUrls?.galleryUrls)
        ? selected.releaseUrls.galleryUrls.filter(Boolean)
        : [];
      if (releaseGallery.length) {
        if (!stop) setGalleryUrls(releaseGallery);
        return;
      }

      if (Array.isArray(selected.galleryPaths) && selected.galleryPaths.length) {
        const urls = [];
        for (const p of selected.galleryPaths) {
          try {
            urls.push(await getDownloadURL(sRef(storage, p)));
          } catch (e) {
            console.warn("[owner][gallery] getDownloadURL failed:", e);
          }
        }
        if (!stop) setGalleryUrls(urls);
      }
    };

    loadAssets();
    return () => {
      stop = true;
    };
  }, [selected]);

  const rows = useMemo(() => {
    const base =
      sub === "pending"
        ? pending
        : sub === "published"
        ? published
        : rejected;
    const s = q.trim().toLowerCase();
    if (!s) return base;
    return base.filter(
      (it) =>
        (it.title || "").toLowerCase().includes(s) ||
        (it.category || "").toLowerCase().includes(s) ||
        (it.ownerUid || "").toLowerCase().includes(s)
    );
  }, [sub, pending, published, rejected, q]);

  // Si non connecté, on arrête ici avec un message explicite
  if (!user?.uid) {
    return (
      <>
        <div className="page-header">
          <h2 className="page-title">Modération Marketplace</h2>
        </div>
        <div className="config-card" style={{ padding: 16 }}>
          Vous n’êtes pas connecté. Connectez-vous pour vérifier votre statut administrateur.
        </div>
      </>
    );
  }

  const onApprove = async (it) => {
    if (
      !window.confirm(
        `Publier « ${it.title || it.id} » et générer la release ?`
      )
    ) {
      return;
    }

    try {
      await auth.currentUser?.getIdToken(true);
      const ok = await checkStorageAdminWrite();
      if (!ok) {
        alert(
          "Accès Storage refusé (admin côté rules non détecté).\n" +
            "Vérifie que le document owner_admins/{TON_UID} existe bien dans Firestore " +
            "ET que tu es connecté avec le même UID."
        );
        return;
      }

      await approveMarketplaceItemWithRelease(it);
      alert("Template publié et assets copiés dans marketplace_items/.");

      // ✅ Reset UI : fermer la prévisualisation et réinitialiser l'état
      setPreviewOpen(false);
      setSelected(null);
      setActivePreviewTab("meta");
      setFormConfig(null);
      setFormValues({});
      setEditing(false);
      setForm({});
    } catch (e) {
      console.error(e);
      alert("Erreur publication/release: " + (e?.message || e));
    }
  };
  const onReject = async (it) => {
    const reason = window.prompt("Raison du rejet :", "");
    if (reason === null) return;
    await updateMarketplaceItemStatus(it.id, "rejected", {
      rejectedReason: reason || "rejeté",
    });
  };

  const onDeleteItem = async (it) => {
    if (!it?.id) return;
    const label = it.title || it.id;

    const ok = window.confirm(
      `Supprimer définitivement l’annonce « ${label} » du marketplace ?\n\n` +
        "Cette action retirera l’élément publié pour tous les utilisateurs."
    );
    if (!ok) return;

    try {
      // Optionnel : refresh du token (utile si tes règles Firestore vérifient owner_admins)
      await auth.currentUser?.getIdToken(true);

      await deleteMarketplaceItem(it.id);
      alert("Template supprimé du marketplace.");
    } catch (e) {
      console.error("[owner][marketplace] delete error:", e);
      alert("Erreur lors de la suppression: " + (e?.message || e));
    }
  };

  const loadPreview = async (item) => {
    setSelected(item);
    setPreviewOpen(true);
    setActivePreviewTab("meta");
    setFormValues({});
    setFormConfig(null);
    try {
      const cfg = await makeFormConfigFromItemForPreview(item);
      setFormConfig(cfg);
      if (!cfg) {
        console.warn(
          "[owner][preview] Aucun configSnapshot attaché; onglet Formulaire vide."
        );
      }
    } catch (e) {
      console.error("[owner][preview] chargement config snapshot échoué:", e);
    }
  };

  const openPreview = (item) => {
    setEditing(false);
    setForm({});
    loadPreview(item);
  };

  const openEdit = (item) => {
    setEditing(true);
    setForm({
      title: item.title || "",
      subtitle: item.subtitle || "",
      category: item.category || "",
      description: item.description || "",
      isPremium: !!item.isPremium,
      price: item.price ?? 0,
      tags: Array.isArray(item.tags) ? item.tags.join(", ") : item.tags || "",
    });
    loadPreview(item);
  };

  const saveEdit = async () => {
    if (!selected?.id) return;
    const patch = {
      title: (form.title || "").trim(),
      subtitle: (form.subtitle || "").trim(),
      category: (form.category || "").trim(),
      description: (form.description || "").trim(),
      isPremium: !!form.isPremium,
      price: Number(form.price) || 0,
      tags: (form.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };
    await updateMarketplaceItemFields(selected.id, patch);
    setSelected((prev) =>
      prev && prev.id === selected.id ? { ...prev, ...patch } : prev
    );
    setEditing(false);
    alert("Modifications enregistrées.");
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setSelected(null);
    setEditing(false);
    setForm({});
    setThumbUrl(null);
    setGalleryUrls([]);
    setFormConfig(null);
    setFormValues({});
    setActivePreviewTab("meta");
  };

  const pill = (s) =>
    s === "pending_review"
      ? "status status-pending"
      : s === "published"
      ? "status status-approved"
      : s === "rejected"
      ? "status status-rejected"
      : "status";

  if (!isAdmin) {
    return (
      <>
        <div className="page-header">
          <h2 className="page-title">Modération Marketplace</h2>
        </div>
        <div className="config-card" style={{ padding: 16 }}>
          Accès refusé : ce compte n’est pas admin.
          <br />Crée le document <code>owner_admins/{user.uid}</code> en racine Firestore.
          {adminDebug && (
            <pre
              style={{
                marginTop: 12,
                background: "#111",
                color: "#9fe",
                padding: 8,
                borderRadius: 6,
              }}
            >
{JSON.stringify(adminDebug, null, 2)}
            </pre>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Modération Marketplace</h2>
        <div className="page-actions">
          <button
            className="btn"
            onClick={async () => {
              try {
                await auth.currentUser?.getIdToken(true);
                await checkStorageUserWrite(auth.currentUser?.uid);
                alert("OK: écriture dossier privé vendeur");
              } catch (e) {
                alert("KO: private write — " + (e?.message || e));
              }
            }}
          >
            Tester écriture vendeur
          </button>
          <button
            className="btn"
            onClick={async () => {
              try {
                await auth.currentUser?.getIdToken(true);
                await checkStorageAdminWrite();
                alert("OK: écriture marketplace_items (admin)");
              } catch (e) {
                alert("KO: release write — " + (e?.message || e));
              }
            }}
          >
            Tester écriture release
          </button>
          <button
            className="btn"
            onClick={async () => {
              try {
                const app = (await import("firebase/app")).getApp();
                const { auth, db, storage } = await import("../../../firebase/firebase");
                const { doc, getDoc } = await import("firebase/firestore");
                const { ref: sRef } = await import("firebase/storage");
                const u = auth.currentUser;
                const effBucket = sRef(storage, "/").bucket;
                const snap = u ? await getDoc(doc(db, "owner_admins", u.uid)) : null;
                const exists = !!snap && snap.exists();
                console.log("[admin-debug]", {
                  uid: u?.uid || null,
                  email: u?.email || null,
                  effBucket,
                  ownerDocExists: exists,
                });
                alert(
                  `UID=${u?.uid || "null"}\n` +
                    `email=${u?.email || "?"}\n` +
                    `bucket=${effBucket}\n` +
                    `owner_admins/${u?.uid}: ${exists ? "EXISTE" : "ABSENT"}`
                );
              } catch (e) {
                console.error(e);
                alert("Debug admin a échoué: " + (e?.message || e));
              }
            }}
          >
            Debug admin
          </button>
          <button
            className={`btn ${sub === "pending" ? "btn-primary" : "btn-outline"}`}
            onClick={() => setSub("pending")}
          >
            <i className="fas fa-hourglass-half"></i> Pending
          </button>
          <button
            className={`btn ${
              sub === "published" ? "btn-primary" : "btn-outline"
            }`}
            onClick={() => setSub("published")}
          >
            <i className="fas fa-check"></i> Published
          </button>
          <button
            className={`btn ${sub === "rejected" ? "btn-primary" : "btn-outline"}`}
            onClick={() => setSub("rejected")}
          >
            <i className="fas fa-ban"></i> Rejected
          </button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            {sub === "pending"
              ? "Soumissions en attente"
              : sub === "published"
              ? "Annonces publiées"
              : "Annonces rejetées"}
          </h3>
          <div className="table-filters">
            <div className="search-box">
              <i className="fas fa-search search-icon" />
              <input
                className="search-input"
                placeholder="Rechercher titre/catégorie/vendeur..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Titre</th>
              <th>Catégorie</th>
              <th>Vendeur (UID)</th>
              <th>Statut</th>
              <th>Créé</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Aucun élément.
                </td>
              </tr>
            )}
            {rows.map((it) => (
              <tr key={it.id}>
                <td>{it.title || "—"}</td>
                <td>{it.category || "—"}</td>
                <td>{it.ownerUid || "—"}</td>
                <td>
                  <span className={pill(it.status)}>{it.status}</span>
                </td>
                <td>
                  {it.createdAt?.toDate
                    ? it.createdAt.toDate().toLocaleString()
                    : "—"}
                </td>
                <td className="actions">
                  <button
                    className="action-btn"
                    title="Aperçu"
                    onClick={() => openPreview(it)}
                  >
                    <i className="fas fa-eye" />
                  </button>
                  <button
                    className="action-btn"
                    title="Éditer"
                    onClick={() => openEdit(it)}
                  >
                    <i className="fas fa-pen" />
                  </button>
                  <button
                    className="action-btn"
                    title="Tester (.docx)"
                    onClick={() => onTest(it)}
                    disabled={!it?.release?.docxPath && !it?.templatePath}
                  >
                    <i className="fas fa-download" />
                  </button>

                  {sub === "pending" && (
                    <>
                      <button
                        className="action-btn"
                        title="Publier"
                        onClick={() => onApprove(it)}
                      >
                        <i className="fas fa-check" />
                      </button>
                      <button
                        className="action-btn danger"
                        title="Rejeter"
                        onClick={() => onReject(it)}
                      >
                        <i className="fas fa-times" />
                      </button>
                    </>
                  )}

                  {sub === "published" && (
                    <button
                      className="action-btn danger"
                      title="Supprimer (retirer du marketplace)"
                      onClick={() => onDeleteItem(it)}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {previewOpen && selected && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closePreview}
        >
          <div
            className="modal-card"
            style={{
              width: "min(920px, 95vw)",
              maxHeight: "85vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,.25)",
              padding: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>
                {editing ? "Éditer la soumission" : "Aperçu de la soumission"}
              </h3>
              <button className="btn btn-outline" onClick={closePreview}>
                <i className="fas fa-times" /> Fermer
              </button>
            </div>

            <div
              className="tabs"
              style={{ display: "flex", gap: 8, marginBottom: 12 }}
            >
              <button
                className={`btn btn-outline ${
                  activePreviewTab === "meta" ? "active" : ""
                }`}
                onClick={() => setActivePreviewTab("meta")}
              >
                <i className="fas fa-info-circle" /> Détails
              </button>
              <button
                className={`btn btn-outline ${
                  activePreviewTab === "form" ? "active" : ""
                }`}
                onClick={() => setActivePreviewTab("form")}
                disabled={!formConfig}
                title={
                  formConfig
                    ? "Voir le formulaire"
                    : "Aucun configSnapshot dans cette publication"
                }
              >
                <i className="fas fa-list" /> Formulaire
              </button>
              <button
                className={`btn btn-outline ${
                  activePreviewTab === "json" ? "active" : ""
                }`}
                onClick={() => setActivePreviewTab("json")}
                disabled={!formConfig}
              >
                <i className="fas fa-code" /> JSON
              </button>
            </div>

            {activePreviewTab === "meta" && (
              <>
                {!editing ? (
                  <>
                    {thumbUrl && (
                      <img
                        alt="Vignette"
                        src={thumbUrl}
                        style={{
                          maxWidth: "100%",
                          borderRadius: 8,
                          marginBottom: 12,
                        }}
                      />
                    )}
                    {galleryUrls.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <b>Galerie</b>
                        <div className="template-gallery">
                          {galleryUrls.map((url, idx) => (
                            <div
                              key={`${url}-${idx}`}
                              className="template-gallery-item"
                            >
                              <img
                                src={url}
                                alt={`Capture ${idx + 1}`}
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div
                      className="grid"
                      style={{
                        display: "grid",
                        gap: 12,
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      <div>
                        <div>
                          <b>Titre</b>: {selected.title || "—"}
                        </div>
                        <div>
                          <b>Sous-titre</b>: {selected.subtitle || "—"}
                        </div>
                        <div>
                          <b>Catégorie</b>: {selected.category || "—"}
                        </div>
                        <div>
                          <b>Statut</b>:{" "}
                          <span className={pill(selected.status)}>
                            {selected.status}
                          </span>
                        </div>
                        <div>
                          <b>Premium</b>: {selected.isPremium ? "Oui" : "Non"}
                        </div>
                        <div>
                          <b>Prix</b>: {selected.price ?? 0}
                        </div>
                      </div>
                      <div>
                        <div>
                          <b>Propriétaire</b>: {selected.ownerUid}
                        </div>
                        <div>
                          <b>Tags</b>:{" "}
                          {Array.isArray(selected.tags)
                            ? selected.tags.join(", ")
                            : selected.tags || "—"}
                        </div>
                        <div>
                          <b>Langues</b>:{" "}
                          {Array.isArray(selected.languages)
                            ? selected.languages.join(", ")
                            : selected.languages || "—"}
                        </div>
                        <div>
                          <b>Juridictions</b>:{" "}
                          {Array.isArray(selected.jurisdictions)
                            ? selected.jurisdictions.join(", ")
                            : selected.jurisdictions || "—"}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <b>Description</b>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {selected.description || "—"}
                      </div>
                    </div>
                    <div
                      className="row-actions"
                      style={{ marginTop: 12, display: "flex", gap: 8 }}
                    >
                      <button
                        className="btn btn-outline"
                        onClick={() => onTest(selected)}
                        disabled={
                          !selected?.release?.docxPath &&
                          !selected?.templatePath
                        }
                      >
                        <i className="fas fa-download" /> Télécharger le .docx
                      </button>
                    </div>
                    <div
                      className="row-actions"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 8,
                      }}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={() => onApprove(selected)}
                        disabled={selected.status !== "pending_review"}
                      >
                        <i className="fas fa-check" /> Approuver & publier
                      </button>
                      <button
                        className="btn btn-outline"
                        onClick={() => onReject(selected)}
                        disabled={selected.status !== "pending_review"}
                      >
                        <i className="fas fa-ban" /> Rejeter
                      </button>
                      <button
                        className="btn"
                        onClick={() => onTest(selected)}
                        disabled={
                          !selected?.release?.docxPath &&
                          !selected?.templatePath
                        }
                      >
                        <i className="fas fa-file-word" /> Tester (DOCX)
                      </button>
                      <button className="btn" onClick={() => onViewJson(selected)}>
                        <i className="fas fa-code" /> Voir JSON
                      </button>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 16,
                        justifyContent: "flex-end",
                      }}
                    >
                      <button className="btn" onClick={() => openEdit(selected)}>
                        <i className="fas fa-pen" /> Éditer la demande
                      </button>
                      {selected.status === "pending_review" && (
                        <button
                          className="btn btn-primary"
                          onClick={() => onApprove(selected)}
                        >
                          <i className="fas fa-check" /> Publier
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="grid"
                      style={{
                        display: "grid",
                        gap: 12,
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      <label>
                        Titre
                        <input
                          className="input"
                          value={form.title}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        Sous-titre
                        <input
                          className="input"
                          value={form.subtitle}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              subtitle: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        Catégorie
                        <input
                          className="input"
                          value={form.category}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              category: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        Prix
                        <input
                          className="input"
                          type="number"
                          step="0.01"
                          value={form.price}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              price: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        Premium
                        <input
                          type="checkbox"
                          checked={!!form.isPremium}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              isPremium: e.target.checked,
                            }))
                          }
                        />
                      </label>
                      <label>
                        Tags (séparés par des virgules)
                        <input
                          className="input"
                          value={form.tags}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              tags: e.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                    <label style={{ display: "block", marginTop: 12 }}>
                      Description
                      <textarea
                        className="textarea"
                        rows={6}
                        value={form.description}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 16,
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn btn-outline"
                        onClick={() => setEditing(false)}
                      >
                        Annuler
                      </button>
                      <button className="btn btn-primary" onClick={saveEdit}>
                        <i className="fas fa-save" /> Enregistrer
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {activePreviewTab === "form" && (
              <>
                {!formConfig && (
                  <div className="alert">
                    Aucun instantané de configuration n’est attaché à cette
                    publication. Demande au vendeur de republier depuis un
                    modèle avec configuration.
                  </div>
                )}
                {formConfig && (
                  <div className="config-card">
                    <div className="config-title">Aperçu du formulaire</div>
                    <DynamicForm
                      fields={previewFields}
                      sections={previewSections}
                      values={formValues}
                      setValues={setFormValues}
                      showPlaceholders={false}
                    />
                  </div>
                )}
              </>
            )}
            {activePreviewTab === "json" && (
              <div className="config-card" style={{ padding: 12 }}>
                {!formConfig ? (
                  <div style={{ opacity: 0.7 }}>
                    Aucune <code>configSnapshot</code> n’est attachée à cet item.
                  </div>
                ) : (
                  <>
                    <div
                      className="row-actions"
                      style={{
                        marginBottom: 8,
                        display: "flex",
                        gap: 8,
                      }}
                    >
                      <button
                        className="btn btn-outline"
                        onClick={copyJSONToClipboard}
                        title="Copier la config"
                      >
                        <i className="fas fa-clipboard" /> Copier
                      </button>
                      <button
                        className="btn btn-outline"
                        onClick={downloadJSON}
                        title="Télécharger config.json"
                      >
                        <i className="fas fa-file-download" /> Télécharger
                      </button>
                    </div>
                    <pre
                      className="code-block"
                      style={{ maxHeight: 320, overflow: "auto" }}
                    >
{JSON.stringify(formConfig, null, 2)}
                    </pre>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {jsonOpen && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1500,
          }}
          onClick={closeJson}
        >
          <div
            className="modal-card"
            style={{
              width: "min(800px, 95vw)",
              maxHeight: "80vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
              padding: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>Configuration JSON</h3>
              <button className="btn btn-outline" onClick={closeJson}>
                <i className="fas fa-times" /> Fermer
              </button>
            </div>
            <pre
              className="code-block"
              style={{ maxHeight: 500, overflow: "auto" }}
            >
{jsonText}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}

